import React, { useState, useEffect } from 'react';
import './BoothPicker.css';

const BoothPicker = ({ 
  gridData = [], 
  selectedBooths = [], 
  onBoothsChange, 
  onClose,
  show = false 
}) => {
  const [localSelection, setLocalSelection] = useState(new Set(selectedBooths));
  
  // Update local selection when selectedBooths prop changes or modal shows
  useEffect(() => {
    if (show) {
      setLocalSelection(new Set(selectedBooths));
    }
  }, [selectedBooths, show]);
  const [filterAvailable, setFilterAvailable] = useState(true);

  if (!show) return null;

  // Calculate grid dimensions
  const maxRow = Math.max(...gridData.map(b => b.row));
  const maxCol = Math.max(...gridData.map(b => b.col));

  const handleBoothClick = (booth) => {
    const newSelection = new Set(localSelection);
    if (newSelection.has(booth.id)) {
      newSelection.delete(booth.id);
    } else {
      newSelection.add(booth.id);
    }
    setLocalSelection(newSelection);
  };

  const handleApply = () => {
    const finalSelection = Array.from(localSelection);
    onBoothsChange(finalSelection);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelection(new Set(selectedBooths));
    onClose();
  };

  const filteredBooths = filterAvailable 
    ? gridData.filter(booth => booth.status === 'Available' || localSelection.has(booth.id))
    : gridData;

  return (
    <div className="booth-picker-overlay">
      <div className="booth-picker-modal">
        <div className="booth-picker-header">
          <h3>📍 Select Booths for Group</h3>
          <div className="picker-controls">
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={filterAvailable}
                onChange={(e) => setFilterAvailable(e.target.checked)}
              />
              Show only available booths
            </label>
            <div className="selection-count">
              {localSelection.size} booth{localSelection.size !== 1 ? 's' : ''} selected
            </div>
          </div>
        </div>

        <div className="booth-picker-content">
          <div className="booth-grid-container">
            <div className="booth-picker-grid">
              {/* Column headers */}
              <div className="grid-header">
                <div className="row-header"></div>
                {Array.from({ length: maxCol }, (_, i) => (
                  <div key={i + 1} className="col-header">
                    {i + 1}
                  </div>
                ))}
              </div>

              {/* Grid rows */}
              {Array.from({ length: maxRow }, (_, row) => (
                <div key={row + 1} className="grid-row">
                  <div className="row-header">{row + 1}</div>
                  {Array.from({ length: maxCol }, (_, col) => {
                    const booth = filteredBooths.find(b => b.row === row + 1 && b.col === col + 1);
                    
                    if (!booth) {
                      return <div key={`${row + 1}-${col + 1}`} className="empty-cell"></div>;
                    }

                    const isSelected = localSelection.has(booth.id);
                    const isAvailable = booth.status === 'Available';
                    const isCorner = booth.corner === 'Y';

                    return (
                      <button
                        key={booth.id}
                        className={`booth-cell ${isSelected ? 'selected' : ''} ${!isAvailable ? 'unavailable' : ''} ${isCorner ? 'corner' : ''}`}
                        onClick={() => handleBoothClick(booth)}
                        disabled={!isAvailable && !isSelected}
                        title={`Booth ${booth.number} (${booth.row}-${booth.col})\nStatus: ${booth.status}\nLevel: ${booth.level}`}
                      >
                        <span className="booth-number">{booth.number}</span>
                        {isSelected && <span className="selection-mark">✓</span>}
                        {isCorner && <span className="corner-mark">📍</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {localSelection.size > 0 && (
            <div className="selected-booths-summary">
              <h4>Selected Booths ({localSelection.size}):</h4>
              <div className="selected-booths-list">
                {Array.from(localSelection)
                  .map(id => gridData.find(b => b.id === id))
                  .filter(Boolean)
                  // Sort by row then column so alphanumeric numbers (e.g., A01) don't break ordering
                  .sort((a, b) => (a.row - b.row) || (a.col - b.col))
                  .map(booth => (
                    <span key={booth.id} className="selected-booth-tag">
                      #{booth.number}
                      <button 
                        className="remove-booth"
                        onClick={() => handleBoothClick(booth)}
                        title="Remove from selection"
                      >
                        ×
                      </button>
                    </span>
                  ))}
              </div>
            </div>
          )}
        </div>

        <div className="booth-picker-actions">
          <button onClick={handleCancel} className="cancel-btn">
            Cancel
          </button>
          <button onClick={handleApply} className="apply-btn">
            Apply Selection ({localSelection.size} booths)
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoothPicker;